import React from "react";

// ToastyView — lets a nested tool declare that IT is the surface the user is
// looking at, so Toasty can resolve a FEATURE-SPECIFIC key deeper than the
// tab/sub-tab that Dashboard computes.
//
// Dashboard's viewKey only knows the top-level tab + sub-tab. Heavy in-tab
// tools (Plan Composer, the IPS builder, Scenario Studio) render inside a tab
// and would otherwise show that tab's generic guide. A tool calls
// `useDeepToastyView("advisor:library:templates:plan_composer")` while mounted;
// Toasty then shows the plan-composer guide (falling back to the parent key if
// no specific entry exists). The override clears automatically on unmount.

type Ctx = { override: string | null; setOverride: React.Dispatch<React.SetStateAction<string | null>> };
const ToastyViewCtx = React.createContext<Ctx>({ override: null, setOverride: () => {} });

export function ToastyViewProvider({ children }: { children: React.ReactNode }) {
  const [override, setOverride] = React.useState<string | null>(null);
  return <ToastyViewCtx.Provider value={{ override, setOverride }}>{children}</ToastyViewCtx.Provider>;
}

/** Read the current feature-specific override (or null). Dashboard uses this to
 *  prefer a tool's deep key over its own computed tab/sub viewKey. */
export function useToastyOverride(): string | null {
  return React.useContext(ToastyViewCtx).override;
}

/** Declare the mounting component as the active Toasty surface. Pass a deep
 *  viewKey while mounted (null/undefined to opt out). Clears on unmount without
 *  clobbering a newer override that may have replaced it. */
export function useDeepToastyView(key: string | null | undefined) {
  const { setOverride } = React.useContext(ToastyViewCtx);
  React.useEffect(() => {
    if (!key) return;
    setOverride(key);
    return () => setOverride(prev => (prev === key ? null : prev));
  }, [key, setOverride]);
}
